import { useEffect, useState, type ReactNode } from "react";
import {
  Body1,
  Caption1,
  Checkbox,
  Input,
  Radio,
  RadioGroup,
  makeStyles,
  mergeClasses,
  tokens,
} from "@fluentui/react-components";
import { Dismiss16Regular } from "@fluentui/react-icons";
import { FuiButton } from "@/components/fui-control";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { confirmReady, type ConfirmTier } from "./confirm-tier";

const useStyles = makeStyles({
  surface: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    minWidth: 0,
    width: "100%",
  },
  header: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "8px",
    flexShrink: 0,
    marginLeft: "-16px",
    marginRight: "-16px",
    paddingLeft: "16px",
    paddingRight: "12px",
    paddingBottom: "4px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  titleGroup: {
    display: "flex",
    alignItems: "center",
    gap: "2px",
    minWidth: 0,
    flex: "1 1 auto",
  },
  titleAside: {
    display: "inline-flex",
    flexShrink: 0,
  },
  headerAction: {
    display: "flex",
    alignItems: "center",
    gap: "2px",
    flexShrink: 0,
    marginLeft: "auto",
  },
  cornerHeader: {
    position: "absolute",
    top: "12px",
    right: "12px",
    zIndex: 2,
  },
  closeBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    padding: 0,
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground3,
    cursor: "pointer",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground1,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  title: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
  },
  lead: {
    color: tokens.colorNeutralForeground3,
  },
  body: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    minWidth: 0,
  },
  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalS,
    marginLeft: "-16px",
    marginRight: "-16px",
    paddingLeft: "16px",
    paddingRight: "16px",
    paddingTop: "8px",
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  footerStart: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    minWidth: 0,
    color: tokens.colorNeutralForeground3,
  },
  footerEnd: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    marginLeft: "auto",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXXS,
    minWidth: 0,
  },
  fieldLabel: {
    color: tokens.colorNeutralForeground2,
  },
  grid2: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: tokens.spacingHorizontalS,
  },
  checkbox: {
    alignSelf: "flex-start",
    alignItems: "center",
  },
  checkboxIndicator: {
    marginTop: 0,
    marginBottom: 0,
    marginLeft: 0,
    marginRight: 0,
  },
  checkboxLabel: {
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: "6px",
    paddingRight: 0,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    color: tokens.colorNeutralForeground1,
  },
  checkboxLabelSmall: {
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground2,
  },
  choices: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "8px",
  },
  choice: {
    alignItems: "flex-start",
  },
  choiceIndicator: {
    marginTop: "2px",
    marginBottom: 0,
    marginLeft: 0,
    marginRight: 0,
  },
  choiceLabel: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "2px",
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: "6px",
    paddingRight: 0,
    minWidth: 0,
  },
  choiceName: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
  },
  choiceHint: {
    color: tokens.colorNeutralForeground3,
  },
  choiceCards: {
    alignItems: "stretch",
    width: "100%",
  },
  choiceColumns: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    alignItems: "stretch",
    width: "100%",
  },
  choiceCard: {
    display: "flex",
    width: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    marginTop: 0,
    marginRight: 0,
    marginBottom: 0,
    marginLeft: 0,
    borderTopWidth: "1px",
    borderRightWidth: "1px",
    borderBottomWidth: "1px",
    borderLeftWidth: "1px",
    borderTopStyle: "solid",
    borderRightStyle: "solid",
    borderBottomStyle: "solid",
    borderLeftStyle: "solid",
    borderTopColor: tokens.colorNeutralStroke1,
    borderRightColor: tokens.colorNeutralStroke1,
    borderBottomColor: tokens.colorNeutralStroke1,
    borderLeftColor: tokens.colorNeutralStroke1,
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground1,
    cursor: "pointer",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ":has(input:checked)": {
      borderTopColor: tokens.colorBrandStroke1,
      borderRightColor: tokens.colorBrandStroke1,
      borderBottomColor: tokens.colorBrandStroke1,
      borderLeftColor: tokens.colorBrandStroke1,
      backgroundColor: tokens.colorBrandBackground2,
    },
    ":has(input:checked):hover": {
      backgroundColor: tokens.colorBrandBackground2Hover,
    },
  },
  choiceIndicatorCard: {
    marginTop: "12px",
    marginLeft: "12px",
  },
  choiceLabelCard: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: "auto",
    alignSelf: "stretch",
    paddingTop: "10px",
    paddingRight: "12px",
    paddingBottom: "10px",
    paddingLeft: "8px",
  },
  choiceTitle: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    minWidth: 0,
  },
  choiceIcon: {
    display: "inline-flex",
    flexShrink: 0,
    color: tokens.colorNeutralForeground2,
  },
});

export type AppDialogWidth = "xs" | "sm" | "md" | "lg" | "xl";
const WIDTH: Record<AppDialogWidth, string> = {
  xs: "max-w-[min(280px,calc(100%-2rem))] sm:max-w-[280px]",
  sm: "max-w-[min(400px,calc(100%-2rem))] sm:max-w-[400px]",
  md: "max-w-[min(480px,calc(100%-2rem))] sm:max-w-[480px]",
  lg: "max-w-[min(640px,calc(100%-2rem))] sm:max-w-[640px]",
  xl: "max-w-[min(860px,calc(100%-2rem))] sm:max-w-[860px]",
};

/**
 * 대화상자 정본. 헤더는 제목 13px 한 줄, 위 8px·아래 4px, 구분선, 제목 바로 오른쪽의 …(있으면), 오른쪽 끝의 닫기 24px.
 * 푸터는 위 구분선, 그 아래 8px, 동작은 24px.
 */
export function AppDialog({
  open,
  onClose,
  title,
  description,
  width = "md",
  children,
  footerStart,
  footer,
  titleAside,
  headerAction,
  className,
  persistWhile,
  modal = true,
  height,
  chrome = "bar",
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  width?: AppDialogWidth;
  children: ReactNode;
  /** left slot of the footer (hints, step counters) */
  footerStart?: ReactNode;
  /** right slot of the footer (actions) */
  footer?: ReactNode;
  /** 제목 글자 바로 오른쪽. 목업 사례. */
  titleAside?: ReactNode;
  /** 닫기 왼쪽. 그 대화상자의 기능 버튼. */
  headerAction?: ReactNode;
  className?: string;
  /** Keep the dialog open while a stacked sheet/drawer is in front (ignore outside dismiss). */
  persistWhile?: boolean;
  /** `false` keeps Fluent OverlayDrawer / MenuPopover opened from inside clickable (no body pointer-events lock). */
  modal?: boolean;
  /** Fixed outer height in px. Body fills the space between header and footer. */
  height?: number;
  /** `corner`는 제목 줄을 두지 않고 닫기만 오른쪽 위에 둔다. 보이는 제목은 본문이 가진다. */
  chrome?: "bar" | "corner";
}) {
  const styles = useStyles();
  const hold = (e: { preventDefault: () => void }) => {
    if (persistWhile) e.preventDefault();
  };
  return (
    <Dialog modal={modal} open={open} onOpenChange={(o) => {
      if (!o && persistWhile) return;
      if (!o) onClose();
    }}>
      {open && (
        <DialogContent
          modal={modal}
          showCloseButton={false}
          overlayClassName={persistWhile ? "pointer-events-none" : undefined}
          style={height != null ? { height } : undefined}
          className={cn(WIDTH[width], "gap-0 overflow-hidden bg-white px-4 pt-[8px] pb-4 dark:bg-background", height != null && "flex flex-col", className)}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => {
            if (persistWhile || document.activeElement?.closest("[data-dialog-local-escape]")) e.preventDefault();
          }}
          onPointerDownOutside={hold}
          onFocusOutside={hold}
          onInteractOutside={hold}
        >
          <div className={styles.surface} style={height != null ? { flex: 1, minHeight: 0 } : undefined}>
            <header className={chrome === "corner" ? styles.cornerHeader : styles.header}>
              {chrome === "bar" ? (
                <div className={styles.titleGroup}>
                  <DialogTitle className={styles.title}>{title}</DialogTitle>
                  {titleAside ? <div className={styles.titleAside}>{titleAside}</div> : null}
                </div>
              ) : (
                <DialogTitle className="sr-only">{title}</DialogTitle>
              )}
              {chrome === "bar" ? (
                <div className={styles.headerAction}>
                  {headerAction}
                  <DialogClose className={styles.closeBtn} aria-label="닫기">
                    <Dismiss16Regular />
                  </DialogClose>
                </div>
              ) : (
                <DialogClose className={styles.closeBtn} aria-label="닫기">
                  <Dismiss16Regular />
                </DialogClose>
              )}
            </header>
            {description ? (
              <DialogDescription className={styles.lead}>{description}</DialogDescription>
            ) : (
              <DialogDescription className="sr-only">{typeof title === "string" ? title : "dialog"}</DialogDescription>
            )}
            <div className={styles.body} style={height != null ? { flex: 1, minHeight: 0, overflow: chrome === "corner" ? "hidden" : "auto" } : undefined}>{children}</div>
            {(footer || footerStart) && (
              <footer className={styles.footer}>
                <div className={styles.footerStart}>{footerStart}</div>
                <div className={styles.footerEnd}>{footer}</div>
              </footer>
            )}
          </div>
        </DialogContent>
      )}
    </Dialog>
  );
}

/** 이름이 있는 설정. 각 선택 아래에 그 결과를 고정한다. `cards`는 테두리로 고른 값을 구분한다. */
export function DialogChoiceGroup<T extends string>({
  label,
  value,
  onChange,
  options,
  ariaLabel,
  bare = false,
  cards = false,
  columns = 1,
}: {
  label: ReactNode;
  value: T;
  onChange: (value: T) => void;
  ariaLabel?: string;
  /** 바깥 행이 이름을 가질 때 필드 라벨을 빼 이중 제목을 막는다. */
  bare?: boolean;
  /** 선택마다 테두리. 고른 값은 브랜드 선이다. */
  cards?: boolean;
  /** 2면 같은 무게의 선택을 나란히 둔다. */
  columns?: 1 | 2;
  options: Array<{ value: T; label: string; hint: string; icon?: ReactNode }>;
}) {
  const styles = useStyles();
  const group = (
      <RadioGroup
        className={mergeClasses(styles.choices, cards && styles.choiceCards, columns === 2 && styles.choiceColumns)}
        value={value}
        aria-label={ariaLabel ?? (typeof label === "string" ? label : undefined)}
        onChange={(_, data) => onChange(data.value as T)}
      >
        {options.map((option) => (
          <Radio
            key={option.value}
            className={mergeClasses(styles.choice, cards && styles.choiceCard)}
            value={option.value}
            indicator={{ className: mergeClasses(styles.choiceIndicator, cards && styles.choiceIndicatorCard) }}
            label={{
              className: mergeClasses(styles.choiceLabel, cards && styles.choiceLabelCard),
              children: (
                <>
                  <span className={styles.choiceTitle}>
                    {option.icon ? <span className={styles.choiceIcon}>{option.icon}</span> : null}
                    <span className={styles.choiceName}>{option.label}</span>
                  </span>
                  <Caption1 className={styles.choiceHint}>{option.hint}</Caption1>
                </>
              ),
            }}
          />
        ))}
      </RadioGroup>
  );
  return bare ? group : <DialogField label={label}>{group}</DialogField>;
}

/** Labeled field for use inside AppDialog forms. */
export function DialogField({ label, required, hint, children, className }: { label: ReactNode; required?: boolean; hint?: ReactNode; children: ReactNode; className?: string }) {
  const styles = useStyles();
  return (
    <div className={cn(styles.field, className)}>
      <Caption1 className={styles.fieldLabel}>
        {label}
        {required && <span style={{ color: tokens.colorPaletteRedForeground1 }}> *</span>}
      </Caption1>
      {children}
      {hint && <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{hint}</Caption1>}
    </div>
  );
}

export function DialogGrid2({ children }: { children: ReactNode }) {
  const styles = useStyles();
  return <div className={styles.grid2}>{children}</div>;
}

/** Compact checkbox for dialog forms: no Fluent 8px gutter, so it lines up with field labels and inputs. */
export function DialogCheckbox({ label, checked, onChange, size = "body", disabled }: { label: ReactNode; checked: boolean; onChange: (checked: boolean) => void; size?: "body" | "label"; disabled?: boolean }) {
  const styles = useStyles();
  return (
    <Checkbox
      className={styles.checkbox}
      indicator={{ className: styles.checkboxIndicator }}
      label={{ children: label, className: mergeClasses(styles.checkboxLabel, size === "label" && styles.checkboxLabelSmall) }}
      checked={checked}
      disabled={disabled}
      onChange={(_, d) => onChange(Boolean(d.checked))}
    />
  );
}

/**
 * DialogField whose value only applies when switched on. The label is a checkbox; when off the input stays
 * in place disabled (never hidden) so the layout doesn't jump and the kept value stays visible.
 */
export function DialogToggleField({ label, checked, onCheckedChange, hint, error, children }: { label: ReactNode; checked: boolean; onCheckedChange: (checked: boolean) => void; hint?: ReactNode; error?: ReactNode; children: ReactNode }) {
  const styles = useStyles();
  return (
    <div className={styles.field}>
      <DialogCheckbox size="label" label={label} checked={checked} onChange={onCheckedChange} />
      {children}
      {error ? <Caption1 style={{ color: tokens.colorPaletteRedForeground1 }}>{error}</Caption1> : hint && <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{hint}</Caption1>}
    </div>
  );
}

/** Standard footer actions: 취소 + primary. `disabled` greys out the primary until the form is valid. */
export function DialogActionsBar({
  onCancel,
  onConfirm,
  confirmLabel = "저장",
  cancelLabel = "취소",
  disabled,
  destructive,
}: {
  onCancel: () => void;
  onConfirm: () => void;
  confirmLabel?: string;
  cancelLabel?: string;
  disabled?: boolean;
  destructive?: boolean;
}) {
  return (
    <>
      <FuiButton density="dialog" appearance="secondary" onClick={onCancel}>
        {cancelLabel}
      </FuiButton>
      <FuiButton
        density="dialog"
        appearance="primary"
        disabled={disabled}
        onClick={onConfirm}
        style={destructive && !disabled ? { backgroundColor: tokens.colorPaletteRedBackground3, color: tokens.colorNeutralForegroundOnBrand } : undefined}
      >
        {confirmLabel}
      </FuiButton>
    </>
  );
}

/** Planner execution Task 삭제 확인 본문 (BulkBar·드로어와 동일 문구). 대상 이름은 넣지 않는다. */
export function executionTaskDeleteConfirmMessage(count: number): ReactNode {
  if (count === 1) return <>이 Task를 정말 삭제하시겠습니까?</>;
  return <>선택한 {count}개의 Task를 정말 삭제하시겠습니까?</>;
}

/** WBS 일정 작업 삭제 확인 본문 (툴바·그리드·컨텍스트 메뉴). 대상 이름은 넣지 않는다. */
export function wbsTaskDeleteConfirmMessage(count: number): ReactNode {
  if (count === 1) return <>이 작업을 정말 삭제하시겠습니까?</>;
  return <>선택한 {count}개의 작업을 정말 삭제하시겠습니까?</>;
}

/**
 * Destructive confirmation. Same header as every other dialog: title, rule, close.
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "삭제",
  cancelLabel = "취소",
  onCancel,
  onConfirm,
  width,
  tier = "T1",
  expectedName = "",
  impacts,
}: {
  open: boolean;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  /** 닫기 버튼. 확인 동사와 겹치면 결과 말로 바꿉니다. */
  cancelLabel?: string;
  onCancel: () => void;
  onConfirm: (detail: { reason: string }) => void;
  /**
   * 예전에는 부모 대화상자 안에서만 Radix로 그렸다. 지금은 항상 AppDialog 헤더를 쓴다.
   * 호출부가 넘겨도 동작은 같다.
   */
  stacked?: boolean;
  width?: AppDialogWidth;
  /** T1은 바로 확인. T2는 이름을 그대로 입력. T3는 사유와 5초. */
  tier?: ConfirmTier;
  expectedName?: string;
  impacts?: string[];
}) {
  const [typedName, setTypedName] = useState("");
  const [reason, setReason] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(5);
  useEffect(() => {
    if (!open) return;
    setTypedName("");
    setReason("");
    setSecondsLeft(5);
  }, [open]);
  useEffect(() => {
    if (!open || tier !== "T3" || secondsLeft <= 0) return;
    const timer = setTimeout(() => setSecondsLeft((left) => left - 1), 1000);
    return () => clearTimeout(timer);
  }, [open, tier, secondsLeft]);
  const ready = confirmReady({ tier, typedName, expectedName, reason, secondsLeft });
  const body = (
    <ConfirmExtra
      message={message}
      tier={tier}
      expectedName={expectedName}
      impacts={impacts}
      typedName={typedName}
      reason={reason}
      secondsLeft={secondsLeft}
      onTypedName={setTypedName}
      onReason={setReason}
    />
  );
  const actions = (
    <DialogActionsBar
      onCancel={onCancel}
      onConfirm={() => onConfirm({ reason })}
      cancelLabel={cancelLabel}
      confirmLabel={confirmLabel}
      disabled={!ready}
      destructive
    />
  );
  return (
    <AppDialog open={open} onClose={onCancel} title={title} width={width ?? "sm"} footer={actions}>
      {body}
    </AppDialog>
  );
}

function ConfirmExtra({
  message,
  tier,
  expectedName,
  impacts,
  typedName,
  reason,
  secondsLeft,
  onTypedName,
  onReason,
}: {
  message: ReactNode;
  tier: ConfirmTier;
  expectedName: string;
  impacts?: string[];
  typedName: string;
  reason: string;
  secondsLeft: number;
  onTypedName: (value: string) => void;
  onReason: (value: string) => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: tokens.spacingVerticalS }}>
      <Body1>{message}</Body1>
      {impacts && impacts.length > 0 ? (
        <ul style={{ margin: 0, paddingLeft: tokens.spacingHorizontalL }}>
          {impacts.map((line) => (
            <li key={line}>
              <Caption1>{line}</Caption1>
            </li>
          ))}
        </ul>
      ) : null}
      {tier === "T2" ? (
        <DialogField label={`확인하려면 「${expectedName}」을(를) 입력하세요`}>
          <Input value={typedName} onChange={(_, data) => onTypedName(data.value)} />
        </DialogField>
      ) : null}
      {tier === "T3" ? (
        <>
          <DialogField label="사유">
            <Input value={reason} onChange={(_, data) => onReason(data.value)} />
          </DialogField>
          <Caption1>{secondsLeft > 0 ? `${secondsLeft}초 뒤에 확인할 수 있습니다.` : "확인할 수 있습니다."}</Caption1>
          <Caption1>다시 로그인은 아직 없습니다.</Caption1>
        </>
      ) : null}
    </div>
  );
}
